import { useQuery } from '@tanstack/react-query'
import { CheckinsService } from '@/shared/api/rest/services/checkins.service'
import { adaptFindAllCheckins } from '@/shared/api/rest/checkins.adapters'

/** One check-in row, narrowed to the fields the history UI reads. */
export type ChildCheckinRecord = {
    _id: string
    checkedIn?: boolean
    checkedOut?: boolean
    checkedInDate?: string | null
    checkedOutDate?: string | null
    session?: string | null
    checkinCode?: string | null
    branchId?: string | null
}

const toRecord = (row: Record<string, unknown>): ChildCheckinRecord => ({
    _id: String(row._id ?? ''),
    checkedIn: Boolean(row.checkedIn),
    checkedOut: Boolean(row.checkedOut),
    checkedInDate: (row.checkedInDate as string) ?? null,
    checkedOutDate: (row.checkedOutDate as string) ?? null,
    session: (row.session as string) ?? null,
    checkinCode: (row.checkinCode as string) ?? null,
    branchId: (row.branchId as string) ?? null
})

/**
 * A single child's recent check-in history, keyed off the scanned QR code.
 *
 * `openCheckin` is the most recent record with no check-out — the "unmatched
 * pair" that has to be closed before the child can be checked in again
 * (the backend rejects a second check-in while one is open).
 */
export const useChildCheckinHistory = (
    qrCode: string | null | undefined,
    limit: number,
    // The scanner wants nothing cached (a stale record would drive the wrong
    // button); browsing the registry can reuse results while clicking around.
    options: { enabled?: boolean; staleTime?: number } = {}
) => {
    const staleTime = options.staleTime ?? 0
    const query = useQuery({
        queryKey: ['child-checkin-history', qrCode, limit],
        enabled: !!qrCode && options.enabled !== false,
        staleTime,
        gcTime: staleTime,
        queryFn: async () => {
            const raw = await CheckinsService.list({
                qrCode: qrCode as string,
                limit,
                sortBy: 'checkedInDate',
                sortOrder: 'DESC'
            })
            return adaptFindAllCheckins(raw).checkInResults.map(toRecord)
        }
    })

    const history = query.data ?? []

    return {
        history,
        openCheckin: history.find((record) => !record.checkedOut) ?? null,
        loading: query.isLoading,
        refetch: query.refetch
    }
}
